import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function SpinWinPromoPopup({ config, onDismiss, onSubmit, submitting, isLoggedIn }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    const data = {};
    if (config.show_email_field) data.email = email;
    if (config.show_phone_field) data.phone = phone;
    const result = await onSubmit(data);
    if (result !== false) setSubmitted(true);
  };

  const handleSpinNow = () => {
    onDismiss();
    if (config.spin_win_event_id) {
      window.location.href = `/spin-and-win?eventId=${config.spin_win_event_id}`;
    } else {
      window.location.href = "/spin-and-win";
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="popup-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
      >
        <motion.div
          key="popup-card"
          initial={{ scale: 0.92, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 30 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
          className="relative bg-[#17121f] border border-[#26202e] rounded-2xl w-[380px] max-w-[calc(100vw-32px)] flex flex-col items-center overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            <div
              className="absolute bottom-0 left-0 right-0 h-[400px]"
              style={{ backgroundImage: "linear-gradient(270deg, #17121f 2.2%, #7e25f7 31.5%, #f0abfc 68%, #17121f 97.2%)" }}
            />
            <div className="absolute inset-0 backdrop-blur-[50px] bg-[rgba(11,4,21,0.5)]" />
          </div>

          <button
            onClick={onDismiss}
            className="absolute top-3 right-3 z-20 text-white/40 hover:text-white transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {submitted ? (
            <div className="relative z-10 flex flex-col items-center gap-4 py-10 px-6 text-center w-full">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.1 }}
                className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center"
              >
                <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </motion.div>
              <h3 className="text-white text-xl font-semibold">Ready to spin!</h3>
              <button
                onClick={handleSpinNow}
                className="bg-gradient-to-b from-[#7e25f7] to-[#590fc0] border border-[#7e25f7] text-white font-bold text-[15px] py-3 px-8 rounded-xl shadow-lg shadow-purple-600/30 hover:brightness-110 transition-all"
              >
                Spin & Win Now
              </button>
            </div>
          ) : (
            <>
              <div className="relative z-10 w-full pt-6 px-6 pb-0 text-center">
                <motion.div
                  initial={{ opacity: 0, y: -10, rotate: -10 }}
                  animate={{ opacity: 1, y: 0, rotate: 0 }}
                  className="flex justify-center mb-3"
                >
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-b from-[#7e25f7] to-[#590fc0] flex items-center justify-center shadow-lg shadow-purple-600/40 animate-pulse">
                      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                      </svg>
                    </div>
                    <div className="absolute -top-1 -right-1">
                      <span className="flex h-5 w-5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-5 w-5 bg-purple-500" />
                      </span>
                    </div>
                  </div>
                </motion.div>

                <h2 className="text-[22px] font-semibold text-white leading-tight">
                  {config.title || "Spin & Win!"}
                </h2>
                {config.subtitle && (
                  <p className="text-white/50 text-[14px] mt-1.5">{config.subtitle}</p>
                )}

                {/* Prize preview */}
                <div className="flex justify-center gap-2 mt-3">
                  {["Free Tickets", "Drinks", "Guest List", "Discounts"].map((prize) => (
                    <span
                      key={prize}
                      className="text-[10px] font-medium text-purple-300/80 bg-purple-600/10 border border-purple-500/20 rounded-full px-2 py-0.5"
                    >
                      {prize}
                    </span>
                  ))}
                </div>
              </div>

              {(config.show_email_field || config.show_phone_field) && (
                <div className="relative z-10 w-full px-6 pt-4 pb-1 space-y-3">
                  {config.show_email_field && (
                    <input
                      className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                      placeholder="Email (optional)"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  )}
                  {config.show_phone_field && (
                    <input
                      className="w-full bg-white/5 border border-white/10 focus:border-green-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                      placeholder="Phone (optional)"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  )}
                </div>
              )}

              <div className="relative z-10 w-full px-6 pt-2 pb-6 space-y-3">
                <button
                  onClick={handleSpinNow}
                  className="w-full bg-gradient-to-b from-[#7e25f7] to-[#590fc0] border border-[#7e25f7] text-white font-bold text-[16px] py-4 px-6 rounded-xl transition-all duration-200 hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                  </svg>
                  {config.button_text || "Spin to Win!"}
                </button>
                <button
                  onClick={onDismiss}
                  className="w-full text-center text-[13px] text-white/40 hover:text-white/70 transition-colors py-1"
                >
                  No thanks
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
